import { BACKSPACE, DELETE, ENTER, ESCAPE, SHIFT, TAB } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, Component, ElementRef, HostListener, ViewChild, Input} from '@angular/core';
import {
	ControlValueAccessor,
	UntypedFormControl,
	UntypedFormGroup,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
	Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AppSettingsService } from '@woolworthsnz/styleguide';
import * as dayjs from 'dayjs';
import { distinctUntilChanged, skip } from 'rxjs/operators';
import { dateValidator } from '../../validators';

@UntilDestroy()
@Component({
	selector: 'form-dob-input',
	templateUrl: './dob-input.component.html',
	styleUrls: ['./dob-input.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			useExisting: DobInputComponent,
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			// eslint-disable-next-line @typescript-eslint/no-use-before-define
			useExisting: DobInputComponent,
			multi: true,
		},
	],
})
export class DobInputComponent implements  ControlValueAccessor, Validator {
	@ViewChild('day', { static: true }) dayField: ElementRef;
	@ViewChild('month', { static: true }) monthField: ElementRef;
	@ViewChild('year', { static: true }) yearField: ElementRef;
	@Input() validateMinimumAge = true;
	isOver18CheckboxEnabled: boolean;
	onTouched: Function;

	get dayName() {
		return 'dobDay';
	}

	get monthName() {
		return 'dobMonth';
	}

	get yearName() {
		return 'dobYear';
	}

	get isOver18Name() {
		return 'isOver18';
	}

	get isOver18(): boolean {
		return this.isOver18CheckboxEnabled && this.dobForm.valid && this.calculateAge() >= 18;
	}

	constructor(private appSettingsService: AppSettingsService) {}

	// eslint-disable-next-line @typescript-eslint/member-ordering
	dobForm = new UntypedFormGroup(
		{
			[this.dayName]: new UntypedFormControl('', {
				validators: [Validators.required, Validators.min(1),Validators.minLength(2), Validators.max(31)],
			}),
			[this.monthName]: new UntypedFormControl('', {
				validators: [Validators.required, Validators.min(1),Validators.minLength(2), Validators.max(12)],
			}),
			[this.yearName]: new UntypedFormControl('', {
				validators: [Validators.required, Validators.min(1901), Validators.max(new Date().getFullYear())],
			}),
		}
	);

	@HostListener('keyup', ['$event.target.name', '$event.target.value', '$event.keyCode'])
	handleKeyUp(name: string, value: string | any[], keyCode: number) {

		if (
			value.length < 2 ||
			name === this.yearName ||
			[DELETE, BACKSPACE, TAB, ESCAPE, ENTER, SHIFT].indexOf(keyCode) !== -1
		) {
			return;
		}

		if (name === this.dayName) {
			this.monthField.nativeElement.focus();
		} else if (name === this.monthName) {
			this.yearField.nativeElement.focus();
		}
	}

	calculateAge(): number {
		const dob = dayjs()
			.date(this.dobForm.get(this.dayName)?.value)
			.month(this.dobForm.get(this.monthName)?.value - 1)
			.year(this.dobForm.get(this.yearName)?.value);

		return Math.abs(dob.diff(dayjs(), 'year', true));
	}


	ngAfterViewInit() {
		this.dobForm.setValidators(dateValidator(
			this.validateMinimumAge?
			this.appSettingsService.getSetting('styleGuideConfig')?.minimumAge:0
			))
		this.dobForm.valueChanges
			.pipe(untilDestroyed(this), distinctUntilChanged(), skip(1))
			.subscribe(() => this.updateOver18Checkbox());
	}

	updateOver18Checkbox() {
		// If the form is currently unchecked (and valid and age is over 18), then go ahead and check it
		if (this.dobForm.valid && !this.isOver18CheckboxChecked() && this.isOver18) {
			this.dobForm.controls[this.isOver18Name].patchValue(true, { emitEvent: false });
		}
		// If the form is checked (or invalid or under 18), then go ahead and uncheck it
		else if (this.isOver18CheckboxChecked() && (this.dobForm.invalid || !this.isOver18)) {
			this.dobForm.controls[this.isOver18Name].patchValue(false, { emitEvent: false });
		}
	}

	isOver18CheckboxChecked(): boolean {
		return this.dobForm.controls[this.isOver18Name]?.value;
	}

	writeValue(val: any) {
		if (val) {
			this.dobForm.setValue(val, { emitEvent: true });
		}
	}

	registerOnChange(fn: any) {
		this.dobForm.valueChanges.subscribe(fn);
	}

	registerOnTouched(fn: Function) {
		this.onTouched = fn;
	}

	setDisabledState?(isDisabled: boolean): void {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		isDisabled ? this.dobForm.disable() : this.dobForm.enable();
	}
	validateDay() {
		const dayValue = this.dobForm.value[this.dayName].trim();
		if(dayValue.length ===1) {
			this.dobForm.controls[this.dayName].setValue(`0${dayValue}`)
		}
		this.dobForm.updateValueAndValidity();
	}

	validateMonth() {
		const monthValue = this.dobForm.value[this.monthName].trim();
		if(monthValue.length ===1) {
			this.dobForm.controls[this.monthName].setValue(`0${monthValue}`)
		}
		this.dobForm.updateValueAndValidity();
	}

	validateYear() {
		this.dobForm.updateValueAndValidity();
	}

	validate(): ValidationErrors | null {
		return this.dobForm.valid ? null : this.dobForm.errors;
	}
}
